import LocationAndRoleSelectorModal from '@app/components/LoggedIn/MyOrganizations/LocationAndRoleSelectorModal';
import { skeletonStyles } from '@app/components/LoggedIn/MyOrganizations/OrganizationCard/styles';
import NavBar from '@app/components/LoggedIn/NavBar';
import Box from '@app/components/common/Box';
import Skeleton from '@app/components/common/Skeleton';
import Typography from '@app/components/common/Typography';
import useUserOrganizations from '@app/hooks/useUserOrganizations';
import { IUserOrganization } from '@app/state/app/types';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { WithTranslation } from 'react-i18next';
import OrganizationCard from '../../../components/LoggedIn/MyOrganizations/OrganizationCard';
import {
  myOrganizationsContainerStyles,
  myOrganizationsStyles,
  organizationsStyles,
} from './styles';
import { useLocation } from 'react-router-dom';
import SearchInput from '@app/components/common/SearchInput';
import { bindActionCreators } from 'redux';
import {
  actionCreatorsApp,
  CONFIRMATION_MODAL_INITIAL_STATE,
  IConfirmationModal,
} from '@westondev/tableturn-core';
import { useDispatch, useSelector } from 'react-redux';
import { acceptInvite } from '@app/state/users/actions';
import { useAppDispatch } from '@app/state/store';
import {
  selectIsSupportUser,
  selectOrganizationsList,
  selectUserDetails,
} from '@app/state/selectors/appSelectors';

const MyOrganizations = ({ t }: WithTranslation) => {
  const { setShowConfirmationModal } = bindActionCreators(
    actionCreatorsApp,
    useDispatch(),
  );

  const dispatch = useAppDispatch();

  const { isLoading } = useUserOrganizations();
  const globalUserDetails = useSelector(selectUserDetails);
  const isSupportUser = useSelector(selectIsSupportUser);
  const organizations = useSelector(selectOrganizationsList);

  const [isRoleSelectorModalActive, setIsRoleSelectorModalActive] = useState<
    number | null
  >(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [organizationsList, setOrganizationsList] = useState(organizations);

  const isAutoSelectOrgDisabled = useLocation().state?.isAutoSelectOrgDisabled;

  const handleAcceptInvite = useCallback(
    (userId: number, orgData: IUserOrganization) => {
      setShowConfirmationModal(CONFIRMATION_MODAL_INITIAL_STATE);
      dispatch(
        acceptInvite(userId, orgData.id, () =>
          setIsRoleSelectorModalActive(orgData.id),
        ),
      );
    },
    [dispatch, setShowConfirmationModal],
  );

  const handleOrganizationCardClick = useCallback(
    (orgData: IUserOrganization) => {
      const isActive = orgData.locations.some(
        location => location.active === true,
      );
      if ((orgData.webPortalAccess && isActive) || isSupportUser) {
        if (orgData.isOrganizationVerified || isSupportUser) {
          setIsRoleSelectorModalActive(orgData.id);
        } else {
          const CONFIRMATION_REQUIRED_MODAL: IConfirmationModal = {
            title: t(
              'loggedIn.myOrganizations.confirmationRequiredModal.title',
            ),
            type: 'info',
            active: true,
            size: 600,
            hideCancel: false,
            autoclose: false,
            btnSuccessText: t(
              'loggedIn.myOrganizations.confirmationRequiredModal.confirmInviteBtn',
            ),
            description: t(
              'loggedIn.myOrganizations.confirmationRequiredModal.description',
              { orgName: orgData.name },
            ),
            onSuccess: () =>
              handleAcceptInvite(globalUserDetails.userId, orgData),
          };
          setShowConfirmationModal(CONFIRMATION_REQUIRED_MODAL);
        }
      } else {
        const CANNOT_ACCESS_ORG_MODAL: IConfirmationModal = {
          title: t('loggedIn.myOrganizations.cannotAccessOrgModal.title'),
          type: 'warning',
          active: true,
          size: 500,
          hideCancel: true,
          componentDescription: (
            <Box
              csx={{
                display: 'flex',
                flexDirection: 'column',
              }}>
              <Typography>
                {t('loggedIn.myOrganizations.cannotAccessOrgModal.description')}
              </Typography>
              <ul>
                <li style={{ marginBottom: '10px' }}>
                  <Typography>
                    {t(
                      'loggedIn.myOrganizations.cannotAccessOrgModal.userInactive',
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography>
                    {t(
                      'loggedIn.myOrganizations.cannotAccessOrgModal.noAccess',
                    )}
                  </Typography>
                </li>
              </ul>
            </Box>
          ),
        };
        setShowConfirmationModal(CANNOT_ACCESS_ORG_MODAL);
      }
    },
    [
      globalUserDetails,
      handleAcceptInvite,
      setShowConfirmationModal,
      isSupportUser,
      t,
    ],
  );

  const handleClearSearch = () => {
    setSearchQuery('');
  };
  const handleSearch = useCallback(
    (query: string) => {
      const newOrganizationList = organizations.filter(org =>
        org.name.toLowerCase().includes(query.toLowerCase().trim()),
      );
      setOrganizationsList(newOrganizationList);
    },
    [organizations],
  );

  useLayoutEffect(() => {
    document.title = 'My Organizations';
  }, []);

  useEffect(() => {
    if (!isAutoSelectOrgDisabled && organizations.length === 1) {
      handleOrganizationCardClick(organizations[0]);
    }
  }, [handleOrganizationCardClick, isAutoSelectOrgDisabled, organizations]);

  useEffect(() => {
    setOrganizationsList(organizations);
  }, [organizations]);

  return (
    <>
      <LocationAndRoleSelectorModal
        isActive={isRoleSelectorModalActive ? true : false}
        onModalClose={() => {
          setIsRoleSelectorModalActive(null);
        }}
        organizationId={isRoleSelectorModalActive || 0}
        isSupportUser={isSupportUser}
      />

      <Box csx={myOrganizationsContainerStyles}>
        <NavBar />
        <Box csx={myOrganizationsStyles}>
          <Typography
            variant="heading"
            fontWeight="medium"
            align="center"
            csx={{ marginBottom: '20px' }}>
            {t('loggedIn.myOrganizations.title')}
          </Typography>
          <Box
            csx={{
              display: 'flex',
              justifyContent: 'center',
            }}>
            <Box
              csx={{
                width: '100%',
                maxWidth: '1240px',
              }}>
              <Box csx={{ marginBottom: '20px' }}>
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onDebounce={handleSearch}
                  onClearPress={handleClearSearch}
                />
              </Box>
              <Box csx={[organizationsStyles]}>
                {isLoading ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={`skeleton_${index}`} csx={skeletonStyles} />
                  ))
                ) : organizationsList.length > 0 ? (
                  <>
                    {organizationsList.map((organization, index) => {
                      const isActive = organization.locations.some(
                        location => location.active === true,
                      );
                      return (
                        <OrganizationCard
                          key={`organization_${organization.id}`}
                          id={organization.id}
                          index={index + 1}
                          name={organization.name}
                          image={organization.digitalLogoUrl}
                          isActive={
                            (isActive &&
                              organization.webPortalAccess &&
                              organization.isOrganizationVerified) ||
                            isSupportUser
                          }
                          onClick={() =>
                            handleOrganizationCardClick(organization)
                          }
                        />
                      );
                    })}
                  </>
                ) : (
                  <Box>
                    <Typography
                      variant="subtitle"
                      fontWeight="medium"
                      align="center"
                      color="darkGrey"
                      csx={{ marginBottom: '15px' }}>
                      {t('loggedIn.myOrganizations.noOrganizations')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MyOrganizations;
